/**
 * TrendingTopics — Prime Pillar's honest "Exploding Topics" for business/SaaS.
 * Powered by real Google Trends 5-year series + first-party mentions + opportunity links.
 * Every number is labelled honestly (interest 0–100, not fake search volume).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Fire, TrendUp, Minus, Snowflake, Mountains, MagnifyingGlass,
  ArrowRight, Sparkle, ArrowClockwise, X, ChartLineUp, Lightning,
} from "@phosphor-icons/react";
import {
  AreaChart, Area, ResponsiveContainer, Tooltip, YAxis,
} from "recharts";
import { TrendingStartups } from "./TrendingStartups";
import TrendingStartupsBoard from "./views/TrendingStartupsBoard";
import TopicsBoard from "./views/TopicsBoard";
import MetaTrends from "./views/MetaTrends";
import FreshnessStamp from "./components/FreshnessStamp";

const TRENDING_FRESH_DOMAIN = {
  topics: "trending_topics",
  meta: "meta_trends",
  startups: "startup_models",
  board: "trending_startups",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  exploding: { label: "Exploding", Icon: Fire, color: "#10b981", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  rising: { label: "Rising", Icon: TrendUp, color: "#3b82f6", chip: "bg-blue-50 text-blue-700 ring-blue-200" },
  regular: { label: "Steady", Icon: Minus, color: "#71717a", chip: "bg-zinc-100 text-zinc-600 ring-zinc-200" },
  peaked: { label: "Peaked", Icon: Mountains, color: "#f59e0b", chip: "bg-amber-50 text-amber-700 ring-amber-200" },
  cooling: { label: "Cooling", Icon: Snowflake, color: "#ef4444", chip: "bg-red-50 text-red-600 ring-red-200" },
  collecting: { label: "Collecting…", Icon: Sparkle, color: "#a1a1aa", chip: "bg-zinc-100 text-zinc-500 ring-zinc-200" },
};

function fmtGrowth(g) {
  if (g === null || g === undefined) return null;
  const neg = g < 0;
  const a = Math.abs(g);
  const sign = neg ? "−" : "+";
  if (a >= 9900) return `${sign}99×+`;
  if (a >= 1000) return `${sign}${Math.round(a / 100)}×`;
  return `${sign}${Math.round(a)}%`;
}

function fmtVolume(v) {
  if (v === null || v === undefined) return null;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e4) return `${Math.round(v / 1e3)}K`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${v}`;
}

const Sparkline = ({ data, color, height = 64 }) => {
  if (!data || data.length < 2) {
    return <div style={{ height }} className="flex items-center justify-center text-[11px] text-zinc-400">chart refreshing…</div>;
  }
  const gid = `g-${color.replace("#", "")}`;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <YAxis hide domain={[0, "dataMax"]} />
        <Tooltip
          contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e4e4e7", padding: "4px 8px" }}
          labelFormatter={(l) => l}
          formatter={(v) => [`${fmtVolume(v)} searches`, "monthly"]}
        />
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${gid})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const Pill = ({ label, value, options, onChange, testid }) => (
  <label className="flex flex-col gap-1" data-testid={testid}>
    <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 text-zinc-800 hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-violet-200 cursor-pointer"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

const StatusBadge = ({ status }) => {
  const s = STATUS_META[status] || STATUS_META.collecting;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${s.chip}`}>
      <Icon size={12} weight="fill" /> {s.label}
    </span>
  );
};

const TopicCard = ({ t, onOpen }) => {
  const s = STATUS_META[t.status] || STATUS_META.collecting;
  const growth = fmtGrowth(t.growth_pct);
  const mentions = (t.mentions && t.mentions.total) || 0;
  const vol = fmtVolume(t.search_volume);
  return (
    <button
      onClick={() => onOpen(t)}
      data-testid={`trending-card-${t.slug}`}
      className="group text-left bg-white border border-zinc-200 rounded-2xl p-5 hover:border-violet-300 hover:shadow-[0_18px_44px_-22px_rgba(124,58,237,0.4)] hover:-translate-y-0.5 transition-all flex flex-col"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold text-zinc-900 text-[15px] leading-tight">{t.topic}</h3>
        <StatusBadge status={t.status} />
      </div>
      <div className="mt-3 flex items-baseline gap-5">
        <div>
          <div className="text-2xl font-bold tabular-nums" style={{ color: s.color }}>{growth || "—"}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">Growth (YoY)</div>
        </div>
        <div>
          <div className="text-2xl font-bold tabular-nums text-zinc-800" data-testid={`trending-volume-${t.slug}`}>{vol || "—"}<span className="text-sm text-zinc-400">/mo</span></div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">Searches</div>
        </div>
      </div>
      <div className="mt-3">
        <Sparkline data={t.series} color={s.color} />
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between text-[12px] text-zinc-500">
        <span className="truncate">{t.category}{mentions > 0 ? ` · ${mentions} mentions` : ""}</span>
        {t.linked_opportunity_count > 0 ? (
          <span className="inline-flex items-center gap-1 text-violet-600 font-medium group-hover:gap-1.5 transition-all">
            {t.linked_opportunity_count} opp{t.linked_opportunity_count > 1 ? "s" : ""} <ArrowRight size={13} weight="bold" />
          </span>
        ) : (
          <span className="text-zinc-300">no linked opps</span>
        )}
      </div>
    </button>
  );
};

const TopicDetail = ({ slug, onClose, onSelectOpp }) => {
  const [t, setT] = useState(null);
  useEffect(() => {
    let on = true;
    axios.get(`${API}/trending/topics/${slug}`).then((r) => on && setT(r.data)).catch(() => {});
    return () => { on = false; };
  }, [slug]);
  if (!t) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 text-zinc-500">Loading…</div>
      </div>
    );
  }
  const s = STATUS_META[t.status] || STATUS_META.collecting;
  const m = t.mentions || {};
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="trending-detail">
        <div className="flex items-start justify-between p-5 sm:p-6 border-b border-zinc-100">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-zinc-900">{t.topic}</h2>
              <StatusBadge status={t.status} />
            </div>
            <p className="mt-1.5 text-sm text-zinc-500 max-w-lg">{t.description}</p>
            <span className="mt-2 inline-block text-[11px] font-mono uppercase tracking-wider text-zinc-400">{t.category}</span>
          </div>
          <button onClick={onClose} data-testid="trending-detail-close" className="p-2 text-zinc-400 hover:text-zinc-700 rounded-lg hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="p-5 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3">
              <div className="text-2xl font-bold tabular-nums" style={{ color: s.color }}>{fmtGrowth(t.growth_5y) || "—"}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Growth (YoY)</div>
            </div>
            <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3" data-testid="trending-detail-volume">
              <div className="text-2xl font-bold tabular-nums text-zinc-800">{fmtVolume(t.search_volume) || "—"}<span className="text-sm text-zinc-400">/mo</span></div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Avg searches</div>
            </div>
            <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3">
              <div className="text-2xl font-bold tabular-nums text-zinc-800">{t.cpc ? `$${Number(t.cpc).toFixed(2)}` : "—"}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">CPC</div>
            </div>
            <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3">
              <div className="text-2xl font-bold tabular-nums text-zinc-800">{m.total || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Mentions</div>
            </div>
          </div>

          <div className="rounded-xl border border-zinc-100 p-3 mb-5">
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Last 12 months — real Google search volume</div>
            <Sparkline data={t.series} color={s.color} height={150} />
            <div className="flex justify-between text-[10px] text-zinc-400 mt-1 px-1">
              {(t.series && t.series.length) ? <><span>{t.series[0].d}</span><span>{t.series[t.series.length - 1].d}</span></> : null}
            </div>
          </div>

          {(m.reddit || m.fiverr || m.freelancer) ? (
            <div className="mb-5">
              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Mentioned across our corpus</div>
              <div className="flex flex-wrap gap-2 text-xs">
                {m.reddit ? <span className="px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 ring-1 ring-orange-200">Reddit · {m.reddit}</span> : null}
                {m.fiverr ? <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">Fiverr · {m.fiverr}</span> : null}
                {m.freelancer ? <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 ring-1 ring-blue-200">Freelancer · {m.freelancer}</span> : null}
              </div>
            </div>
          ) : null}

          {(t.related_queries && t.related_queries.length) ? (
            <div className="mb-5">
              <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Related rising queries</div>
              <div className="flex flex-wrap gap-2 text-xs">
                {t.related_queries.map((q) => <span key={q} className="px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600">{q}</span>)}
              </div>
            </div>
          ) : null}

          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1.5">
              <Lightning size={13} weight="fill" className="text-violet-500" /> Build on this trend — related opportunities
            </div>
            {(t.linked_opportunities && t.linked_opportunities.length) ? (
              <div className="space-y-2">
                {t.linked_opportunities.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => { onSelectOpp && onSelectOpp({ id: o.id, problem: o.problem }); onClose(); }}
                    data-testid={`trending-linked-opp-${o.id}`}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-zinc-200 bg-white hover:border-violet-300 hover:bg-violet-50/40 transition-all"
                  >
                    <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-600/10 text-violet-700 font-bold flex items-center justify-center tabular-nums">{o.score}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-zinc-800 truncate">{o.problem}</span>
                      <span className="block text-[11px] text-zinc-400 truncate">{o.user_type}</span>
                    </span>
                    <ArrowRight size={15} weight="bold" className="text-violet-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400">No scored opportunity matches this topic yet — it may surface on the next scan.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TrendingTopics = ({ onSelectOpp, onNavigate }) => {
  const [view, setView] = useState("topics");
  const [topics, setTopics] = useState([]);
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openSlug, setOpenSlug] = useState(null);
  const [filters, setFilters] = useState({ sort: "volume", timeframe: "5y", category: "All", status: "All", q: "" });

  useEffect(() => {
    axios.get(`${API}/trending/facets`).then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const fetchTopics = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({
      sort: filters.sort, timeframe: filters.timeframe,
      category: filters.category, status: filters.status, limit: "400",
    });
    if (filters.q) p.set("q", filters.q);
    axios.get(`${API}/trending/topics?${p.toString()}`)
      .then((r) => setTopics(r.data || []))
      .catch(() => setTopics([]))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    const id = setTimeout(fetchTopics, filters.q ? 350 : 0);
    return () => clearTimeout(id);
  }, [fetchTopics, filters.q]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/trending/refresh?full=true`);
    } catch (e) { /* non-fatal */ }
    setTimeout(() => { fetchTopics(); axios.get(`${API}/trending/facets`).then((r) => setFacets(r.data)).catch(() => {}); setRefreshing(false); }, 1500);
  };

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const cats = ["All", ...((facets && facets.categories) || [])];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6" data-testid="trending-topics-view">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        {view !== "board" && view !== "topics" && view !== "meta" ? (
        <div>
          <div className="flex items-center gap-2">
            <ChartLineUp size={22} weight="duotone" className="text-violet-600" />
            <h1 className="text-2xl font-bold text-zinc-900">Trending</h1>
          </div>
          <p className="mt-1 text-sm text-zinc-500 max-w-xl">
            What's exploding in <span className="font-medium text-zinc-700">builder &amp; founder demand</span> — ranked by <span className="font-medium text-zinc-700">real Google search volume</span> with 12-month growth. Every number is verifiable. Each trend links to the opportunity to build.
          </p>
        </div>
        ) : <div />}
        <div className="flex items-center gap-3">
          {view === "legacytopics" && facets && facets.last_refreshed ? (
            <span className="text-[11px] text-zinc-400">updated {String(facets.last_refreshed).slice(0, 10)}</span>
          ) : null}
          {view === "legacytopics" ? (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="trending-refresh-btn"
              className="inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-full border border-zinc-200 text-zinc-700 hover:border-violet-300 hover:text-violet-700 transition-all disabled:opacity-50"
            >
              <ArrowClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          ) : null}
        </div>
      </div>

      {/* View toggle: Topics vs Startups & Models */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
      <div className="inline-flex max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden p-1 rounded-full bg-zinc-100" data-testid="trending-view-toggle">
        <button
          onClick={() => setView("topics")}
          data-testid="trending-view-topics"
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${view === "topics" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}
        >
          📈 Topics
        </button>
        <button
          onClick={() => setView("meta")}
          data-testid="trending-view-meta"
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${view === "meta" ? "bg-white text-violet-700 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}
        >
          🧭 Meta Trends
        </button>
        <button
          onClick={() => setView("startups")}
          data-testid="trending-view-startups"
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${view === "startups" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}
        >
          🚀 Startup Models
        </button>
        <button
          onClick={() => setView("board")}
          data-testid="trending-view-board"
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${view === "board" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}
        >
          🚀 Startups
        </button>
      </div>
      <FreshnessStamp domain={TRENDING_FRESH_DOMAIN[view] || "trending_topics"} />
      </div>

      {view === "board" ? (
        <TrendingStartupsBoard />
      ) : view === "meta" ? (
        <MetaTrends />
      ) : view === "startups" ? (
        <TrendingStartups onNavigate={onNavigate} />
      ) : view === "topics" ? (
        <TopicsBoard onAskTrend={() => onNavigate && onNavigate("copilot")} />
      ) : (
      <>
      {/* Status summary */}
      {facets && facets.by_status ? (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="trending-status-summary">
          {["exploding", "rising", "peaked", "cooling", "collecting"].map((st) => {
            const meta = STATUS_META[st]; const { Icon } = meta;
            const n = facets.by_status[st] || 0;
            if (!n) return null;
            return (
              <button key={st} onClick={() => set("status")(filters.status === st ? "All" : st)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ring-1 transition-all ${filters.status === st ? meta.chip + " ring-2" : "bg-white text-zinc-500 ring-zinc-200 hover:ring-zinc-300"}`}>
                <Icon size={13} weight="fill" style={{ color: meta.color }} /> {meta.label} · {n}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-zinc-50/70 rounded-2xl border border-zinc-100">
        <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400">Search</span>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={filters.q}
              onChange={(e) => set("q")(e.target.value)}
              placeholder="Search topics…"
              data-testid="trending-search"
              className="w-full text-sm bg-white border border-zinc-200 rounded-lg pl-9 pr-3 py-2 text-zinc-800 focus:outline-none focus:ring-2 focus:ring-violet-200"
            />
          </div>
        </div>
        <Pill label="Sort by" value={filters.sort} onChange={set("sort")} testid="trending-sort"
          options={[{ value: "volume", label: "Search volume" }, { value: "growth", label: "Growth" }, { value: "mentions", label: "Mentions" }, { value: "opportunities", label: "Opportunities" }]} />
        <Pill label="Category" value={filters.category} onChange={set("category")} testid="trending-category"
          options={cats.map((c) => ({ value: c, label: c }))} />
      </div>

      {/* Grid */}
      {loading && topics.length === 0 ? (
        <div className="text-center py-20 text-zinc-400">Loading topics…</div>
      ) : topics.length === 0 ? (
        <div className="text-center py-20 text-zinc-400" data-testid="trending-empty">No topics match these filters.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trending-grid">
          {topics.map((t) => <TopicCard key={t.slug} t={t} onOpen={(x) => setOpenSlug(x.slug)} />)}
        </div>
      )}

      <p className="mt-8 text-center text-[11px] text-zinc-400 max-w-2xl mx-auto">
        Searches is real Google "avg. monthly searches" and the chart is the last 12 months of actual search volume (via DataForSEO / Google Ads data). Growth compares the most recent 6 months vs the prior 6. Topics marked "Collecting…" are still pulling fresh data.
      </p>
      </>
      )}

      {openSlug ? <TopicDetail slug={openSlug} onClose={() => setOpenSlug(null)} onSelectOpp={onSelectOpp} /> : null}
    </div>
  );
};

export default TrendingTopics;
